import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Type-ahead combobox for selecting a Team Member.
 *
 * Phase 2 (Jun 2026) — replaces a plain shadcn <Select> in the case-create
 * form. As the org grows, scrolling through hundreds of members became
 * painful; type-ahead filters on name, position_id, department, and
 * service_line in a single pass.
 *
 * Client-side filtering only (via cmdk's default matcher). Small team
 * member sets (<500 rows) render fine; if the count ever crosses a few
 * thousand we can add server-side ?q= paging.
 *
 * Props
 *  - members: array of {id, name, position_id, department, service_line}
 *  - value: currently selected id (string) or "" for none
 *  - onChange: (id: string) => void — id of the picked member
 *  - loading: bool — whether the members list is still fetching
 *  - disabled: bool
 *  - placeholder: string
 *  - serviceLineLabel: (code: string) => string — resolves e.g. "GHI" → "Home Office"
 *
 * Accessibility
 *  - The Popover is a Radix menu (role="dialog") housing a cmdk listbox.
 *    ArrowUp/Down navigate options, Enter selects, Escape closes.
 *  - Input has a visible label rendered by the parent form; we also set
 *    aria-label on the trigger button so the trigger is announced.
 *  - Options have data-testid="team-member-combobox-option-{id}" for
 *    Playwright driving.
 */
export default function TeamMemberCombobox({
  members,
  value,
  onChange,
  loading = false,
  disabled = false,
  placeholder = "Search team members by name, position ID, department, or service line…",
  serviceLineLabel = (c) => c,
  testId = "case-team-member",
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  // Match Popover content width to the trigger button so it looks like a
  // proper dropdown rather than a floating menu.
  useEffect(() => {
    if (!triggerRef.current) return;
    const measure = () => setTriggerWidth(triggerRef.current?.offsetWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(triggerRef.current);
    return () => ro.disconnect();
  }, []);

  const selected = useMemo(
    () => members.find((m) => m.id === value) || null,
    [members, value]
  );

  const summary = useMemo(() => {
    if (!selected) return null;
    const parts = [selected.name];
    const pid = selected.position_id || selected.file_number;
    if (pid) parts.push(`#${pid}`);
    if (selected.service_line) parts.push(serviceLineLabel(selected.service_line));
    return parts.join(" · ");
  }, [selected, serviceLineLabel]);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Team member"
          disabled={disabled || loading}
          data-testid={`${testId}-trigger`}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input",
            "bg-transparent px-3 py-1 text-sm shadow-sm transition-colors",
            "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !selected && "text-muted-foreground"
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <Users className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">
              {loading
                ? "Loading team members…"
                : summary || "Select a team member"}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        style={triggerWidth ? { width: triggerWidth } : undefined}
        className="p-0"
        data-testid={`${testId}-popover`}
      >
        <Command
          // Custom keys so cmdk searches across name + position_id + department
          // + service_line simultaneously. Space-joined for phrase matches like
          // "smith GHA".
          filter={(itemValue, search) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={placeholder}
            data-testid={`${testId}-combobox-input`}
            aria-label="Search team members"
          />
          <CommandList>
            <CommandEmpty data-testid={`${testId}-empty`}>
              <div className="py-2 text-sm">
                No active team members match your search.
                <div className="mt-1 text-xs text-muted-foreground">
                  Try a partial name, position ID, or service line code (e.g. "GHI").
                  If you expected to find someone, ask HR Admin to check their
                  Active or Service Line status in the Team Members page.
                </div>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {members.map((m) => {
                const pid = m.position_id || m.file_number || "";
                const sl = m.service_line || "";
                const searchKey = [
                  m.name || "",
                  pid,
                  m.department || "",
                  sl,
                  serviceLineLabel(sl),
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <CommandItem
                    key={m.id}
                    value={searchKey}
                    onSelect={() => {
                      onChange(m.id);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-combobox-option-${m.id}`}
                    className="flex items-start gap-2 py-2"
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        value === m.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {m.name}
                        {pid ? (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            #{pid}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[m.department, serviceLineLabel(sl)]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
